import React, { useState, useEffect } from "react";
import "./Helpdesk.scss";
import storeMenu from "../../../stores";

interface HelpdeskForm {
  name: string;
  email: string;
  subject: string;
  priority: "low" | "medium" | "high" | "urgent";
  category: "technical" | "billing" | "general" | "bug" | "feature";
  description: string;
  attachments: File[];
}

const Helpdesk: React.FC = () => {
  const [formData, setFormData] = useState<HelpdeskForm>({
    name: "",
    email: "",
    subject: "",
    priority: "medium",
    category: "general",
    description: "",
    attachments: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      // storeMenu terhubung ke Menu Main

      // dispatch icon title
      storeMenu.dispatch({ type: "titleicon", text: "Helpdesk" });

      // dispatch breadcrumb path
      storeMenu.dispatch({
        type: "breadcrumbs",
        text: [
          { key: "Support", value: "Help Center" },
          { key: "Help", value: "Helpdesk" },
        ],
      });
    }, 100);
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...filesArray],
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulasi pengiriman data ke server
      await new Promise((resolve) => setTimeout(resolve, 2000));

      console.log("Data tiket helpdesk:", formData);
      setSubmitSuccess(true);

      // Reset form setelah berhasil
      setFormData({
        name: "",
        email: "",
        subject: "",
        priority: "medium",
        category: "general",
        description: "",
        attachments: [],
      });
    } catch (error) {
      console.error("Error submitting ticket:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "low":
        return "#28a745";
      case "medium":
        return "#ffc107";
      case "high":
        return "#fd7e14";
      case "urgent":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  if (submitSuccess) {
    return (
      <div className="helpdesk-container">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h2>Tiket Berhasil Dibuat!</h2>
          <p>
            Tiket helpdesk Anda telah berhasil dikirim. Tim kami akan segera
            menghubungi Anda.
          </p>
          <button
            className="btn-primary"
            onClick={() => setSubmitSuccess(false)}
          >
            Buat Tiket Baru
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="helpdesk-container">
      <div className="helpdesk-header">
        <h1>Helpdesk Support</h1>
        <p>Kirim tiket bantuan untuk mendapatkan dukungan dari tim kami</p>
      </div>

      <div className="helpdesk-content">
        <form onSubmit={handleSubmit} className="helpdesk-form">
          <div className="form-section">
            <h3>Informasi Pribadi</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nama Lengkap *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Masukkan nama lengkap Anda"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Masukkan alamat email Anda"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Detail Tiket</h3>

            <div className="form-group">
              <label htmlFor="subject">Subjek *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                placeholder="Ringkasan singkat masalah Anda"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Kategori *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="general">Umum</option>
                  <option value="technical">Teknis</option>
                  <option value="billing">Tagihan</option>
                  <option value="bug">Bug/Laporan Masalah</option>
                  <option value="feature">Permintaan Fitur</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="priority">Prioritas *</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  required
                >
                  <option value="low">Rendah</option>
                  <option value="medium">Sedang</option>
                  <option value="high">Tinggi</option>
                  <option value="urgent">Mendesak</option>
                </select>
                <div
                  className="priority-indicator"
                  style={{
                    backgroundColor: getPriorityColor(formData.priority),
                  }}
                ></div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Deskripsi Masalah *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={6}
                placeholder="Jelaskan detail masalah Anda secara lengkap..."
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Lampiran (Opsional)</h3>

            <div className="form-group">
              <label htmlFor="attachments">Upload File</label>
              <input
                type="file"
                id="attachments"
                multiple
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              />
              <small>
                Format yang didukung: PDF, DOC, DOCX, JPG, PNG, GIF (Max 5MB per
                file)
              </small>
            </div>

            {formData.attachments.length > 0 && (
              <div className="attachments-list">
                <h4>File yang Dipilih:</h4>
                {formData.attachments.map((file, index) => (
                  <div key={index} className="attachment-item">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => removeFile(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Mengirim..." : "Kirim Tiket"}
            </button>

            <button
              type="button"
              className="btn-reset"
              onClick={() => {
                setFormData({
                  name: "",
                  email: "",
                  subject: "",
                  priority: "medium",
                  category: "general",
                  description: "",
                  attachments: [],
                });
              }}
            >
              Reset Form
            </button>
          </div>
        </form>

        <div className="helpdesk-info">
          <div className="info-card">
            <h3>📞 Kontak Darurat</h3>
            <p>Untuk masalah mendesak, hubungi:</p>
            <ul>
              <li>📱 WhatsApp: +6281234567890</li>
              <li>📧 Email: support@kawal.id</li>
              <li>🕒 Jam Kerja: Senin-Jumat 08:00-17:00 WIB</li>
            </ul>
          </div>

          <div className="info-card">
            <h3>💡 Tips Membuat Tiket</h3>
            <ul>
              <li>Jelaskan masalah dengan detail dan jelas</li>
              <li>Sertakan screenshot jika diperlukan</li>
              <li>Pilih kategori yang sesuai</li>
              <li>Berikan informasi kontak yang valid</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Helpdesk;
